import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ClipboardCheck, ClipboardList, Library, PenLine, Plus, Upload, UserCheck } from "lucide-react";
import { PaperUploadDialog } from "@/components/toolbox/paper-upload-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";
import { TOOLBOX_STATUS, todayLocal, type ToolboxStatus } from "@/lib/toolbox";

export const Route = createFileRoute("/_authenticated/toolbox/")({
  component: ToolboxListPage,
});

interface Row {
  id: string;
  date: string;
  topic: string;
  status: ToolboxStatus;
  projectName: string | null;
  conductedByName: string | null;
  total: number;
  signed: number;
  mySignerId: string | null;
  mySigned: string | null;
}

const FILTERS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "collecting", label: "Collecting signatures" },
  { value: "ready", label: "Ready to send" },
  { value: "sent", label: "Sent" },
] as const;

function ToolboxListPage() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>("all");
  const [paperOpen, setPaperOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["toolbox", "list", filter],
    queryFn: () => apiGet<{ data: Row[]; canCreate: boolean; canAssign?: boolean }>(`/api/toolbox?status=${filter}`),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  // Whoever may hand crew access out (admins, supervisors, foremen) sees the Crew access button
  const { data: grantable } = useQuery({
    queryKey: ["delegations", "projects"],
    queryFn: () => apiGet<{ data: { id: string }[] }>("/api/delegations/projects"),
    staleTime: 60_000,
  });
  const { data: session } = useSession();
  const adminRole = session?.user?.role === "admin" || session?.user?.role === "div_admin";
  const canGive = (grantable?.data.length ?? 0) > 0 || adminRole;
  const { data: assignedData } = useQuery({
    queryKey: ["toolbox", "assigned"],
    queryFn: () => apiGet<{ data: { targetId: string; title: string; dueDate: string | null; note: string | null; assignedByName: string | null; projectName: string | null; meetingId: string | null }[] }>("/api/toolbox/assigned"),
    staleTime: 30_000,
  });
  const assigned = assignedData?.data ?? [];
  const rows = data?.data ?? [];
  // Only supervisors, foremen and admins have the meeting list (drafts, ready to send, sent). Everyone else reads the library.
  useEffect(() => {
    if (data && !data.canCreate) navigate({ to: "/toolbox/library", search: {}, replace: true });
  }, [data, navigate]);
  const toSign = rows.filter((r) => r.mySignerId && !r.mySigned && r.status !== "sent" && r.status !== "completed");

  if (!data || !data.canCreate) {
    return <div className="mx-auto max-w-lg space-y-3 pt-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-24 w-full" /></div>;
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <div className="flex flex-col gap-2 pt-2">
        <h1 className="text-2xl font-bold text-text-primary">Toolbox Meetings</h1>
        {data?.canCreate && (
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/toolbox/library" search={{ tab: "talks" }}>
              <Button size="sm" variant="outline" className="gap-1"><BookOpen className="h-4 w-4" /> Prepared talks</Button>
            </Link>
            <Link to="/toolbox/library" search={{}}>
              <Button size="sm" variant="outline" className="gap-1"><Library className="h-4 w-4" /> Library</Button>
            </Link>
            {canGive && (
              <Link to="/toolbox/access">
                <Button size="sm" variant="outline" className="gap-1"><UserCheck className="h-4 w-4" /> Crew access</Button>
              </Link>
            )}
            {data?.canAssign && (
              <Link to="/toolbox/assignments">
                <Button size="sm" variant="outline" className="gap-1"><ClipboardList className="h-4 w-4" /> Assigned</Button>
              </Link>
            )}
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setPaperOpen(true)}><Upload className="h-4 w-4" /> Paper copy</Button>
            <Link to="/toolbox/new" search={{ edit: undefined }}>
              <Button size="sm" className="gap-1 bg-paris-red hover:bg-paris-red-dark">
                <Plus className="h-4 w-4" /> New
              </Button>
            </Link>
          </div>
        )}
      </div>

      {assigned.length > 0 && (
        <div className="space-y-2 rounded-xl border-2 !border-red-600 !bg-red-100 p-3 dark:!bg-red-950/50">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300">
            <ClipboardList className="h-4 w-4" /> Assigned to you ({assigned.length})
          </p>
          {assigned.map((a) => {
            const overdue = !!a.dueDate && a.dueDate < todayLocal();
            const inner = (
              <>
                <span className="font-medium">{a.title}</span>
                <span className="block text-xs text-muted-foreground">{[a.projectName, a.assignedByName ? `from ${a.assignedByName}` : null].filter(Boolean).join(" · ")}</span>
                <span className={cn("block text-xs", overdue ? "font-semibold text-red-600" : "text-muted-foreground")}>{a.dueDate ? `Due ${formatDateOnly(a.dueDate)}${overdue ? " (overdue)" : ""}` : "No due date"}</span>
                {a.note && <span className="mt-0.5 block whitespace-pre-line text-xs">{a.note}</span>}
                <span className="mt-1 inline-block rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white">{a.meetingId ? "Continue" : "Start"}</span>
              </>
            );
            return a.meetingId ? (
              <Link key={a.targetId} to="/toolbox/$meetingId" params={{ meetingId: a.meetingId }} className="block rounded-lg bg-background/80 px-3 py-2 text-sm hover:bg-background">{inner}</Link>
            ) : (
              <Link key={a.targetId} to="/toolbox/new" search={{ assignment: a.targetId }} className="block rounded-lg bg-background/80 px-3 py-2 text-sm hover:bg-background">{inner}</Link>
            );
          })}
        </div>
      )}

      {toSign.length > 0 && (
        <div className="space-y-2 rounded-xl border border-amber-400/40 bg-amber-500/10 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
            <PenLine className="h-4 w-4" /> {toSign.length} to sign
          </p>
          {toSign.map((r) => (
            <Link key={r.id} to="/toolbox/$meetingId" params={{ meetingId: r.id }} className="block rounded-lg bg-background/70 px-3 py-2 text-sm hover:bg-background">
              <span className="font-medium">{r.topic}</span>
              <span className="block text-xs text-muted-foreground">{[r.projectName, formatDateOnly(r.date)].filter(Boolean).join(" · ")}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2 scrollbar-hide">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={cn(
              "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
              filter === f.value ? "bg-paris-red text-white" : "border border-border bg-card text-text-secondary hover:bg-muted/50 hover:text-text-primary"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      )}

      {!isLoading && rows.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-14 text-center">
            <ClipboardCheck className="mb-3 h-12 w-12 text-muted-foreground/40" />
            <p className="text-lg font-medium text-muted-foreground">No toolbox meetings</p>
            <p className="mt-1 text-sm text-muted-foreground/70">
              {data?.canCreate ? "Start one to record a safety talk with your crew." : "Meetings you are asked to sign will show here."}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((r) => {
          const st = TOOLBOX_STATUS[r.status] ?? TOOLBOX_STATUS.draft;
          return (
            <Link key={r.id} to="/toolbox/$meetingId" params={{ meetingId: r.id }}>
              <Card className="cursor-pointer transition-colors hover:bg-muted/30">
                <CardContent className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.topic}</p>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {[r.projectName, formatDateOnly(r.date), r.conductedByName].filter(Boolean).join(" · ")}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {data?.canCreate && <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", st.cls)}>{st.label}</span>}
                        {r.total > 0 && (
                          <span className="text-[11px] text-muted-foreground">{r.signed}/{r.total} signed</span>
                        )}
                        {r.mySignerId && !r.mySigned && r.status !== "sent" && (
                          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">Your signature needed</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
      <PaperUploadDialog open={paperOpen} onClose={() => setPaperOpen(false)} />
    </div>
  );
}
